import * as React from 'react';
import { Search } from 'lucide-react';
import { t } from '@reos/i18n';
import { V4NextStep } from './ux-v4.js';
import { caseAdvice, type CaseAdvice, type CaseAdviceAction, type CaseAdviceInput } from '../lib/case-advice.js';
import type { CaseEventLine } from '../lib/case-last-event.js';

/**
 * Krok UXR3 — KARTY DETAILU ZAKAZKY: **Další krok** (lidska veta, nejvys dve
 * tlacitka) a **Poslední dění** (tri posledni veci z historie).
 *
 * Krok UXV4a2: karta „Prověření" odsud ZMIZELA — predloha UX v4
 * (`docs/design/ux-v4/project/Zakazka.dc.html`) ma misto ni SEKCI s rozbalenim
 * a problemy s „Proč a co s tím" (`components/case-detail-v4.tsx`). Mit obe by
 * znamenalo dve kopie teze karty; vypocet (`lib/case-review-areas.ts`) se
 * nezmenil a pouziva ho dal jedno misto.
 *
 * Vypocty jsou v `lib/case-advice.ts` — tady je jen vykresleni, aby na obojí
 * sly napsat testy bez prohlizece.
 */

const cs = { locale: 'cs' } as const;

/**
 * Karta „Další krok" — lidska veta a nejvys dve tlacitka (zadani UXR3 bod 5).
 *
 * Krok UXV4a2: kresli ji SDILENA komponenta v4 (`V4NextStep`, predloha
 * `Zakazka.dc.html`: ikona ve ctverci + „DALŠÍ KROK", titul 22, veta, plne a
 * tonovane tlacitko vedle sebe). Logika zustala, kde byla (`lib/case-advice.ts`,
 * UXR5) a `data-testid` se nezmenila — meni se jen sazba.
 */
export function CaseNextStepCard({
  input,
  onAction,
}: {
  readonly input: CaseAdviceInput;
  readonly onAction: (action: CaseAdviceAction) => void;
}): React.JSX.Element {
  const advice: CaseAdvice = caseAdvice(input);
  return (
    <V4NextStep
      testId="case-next-step"
      kicker={t('webAdmin.caseAdvice.kicker', cs)}
      icon={<Search size={18} strokeWidth={2.2} />}
      title={<span data-testid="case-next-step-title">{advice.title}</span>}
      note={advice.body}
      actions={
        advice.actions.length > 0 ? (
          <div className="rh-v4-next__pair">
            {advice.actions.map((action, index) => (
              <button
                key={`${action.kind}-${String(index)}`}
                type="button"
                /* Primarni je JEN prvni akce — druha je tonovana (zadani bod 2:
                   modra = jedina primarni akce na obrazovce). */
                className={index === 0 ? 'rh-v4-btn rh-v4-btn--filled' : 'rh-v4-btn rh-v4-btn--tinted'}
                onClick={() => { onAction(action); }}
                data-testid={index === 0 ? 'case-next-step-primary' : 'case-next-step-secondary'}
              >
                {action.label}
              </button>
            ))}
          </div>
        ) : null
      }
    />
  );
}

/** Karta „Poslední dění" — posledni veci ze SKUTECNE historie zakazky. */
export function CaseLastActivityCard({
  lines,
  formatWhen,
  onOpenHistory,
}: {
  readonly lines: readonly CaseEventLine[];
  readonly formatWhen: (iso: string) => string;
  readonly onOpenHistory: () => void;
}): React.JSX.Element {
  return (
    <section className="rh-a2-card" data-testid="case-last-activity" aria-labelledby="case-last-activity-h">
      <div className="rh-a2-card__head">
        <h2 id="case-last-activity-h">{t('webAdmin.caseHistory.heading', cs)}</h2>
        <button type="button" className="rh-a2-link" onClick={onOpenHistory} data-testid="case-history-link">
          {t('webAdmin.caseHistory.allLink', cs)}
        </button>
      </div>
      {lines.length === 0 ? (
        <p className="rh-a2-empty">{t('webAdmin.caseHistory.empty', cs)}</p>
      ) : (
        <ul className="rh-a2-last" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {lines.map((line) => (
            <li key={line.id} data-testid={`case-event-${line.kind}`}>
              <span className="rh-a2-last__when">{formatWhen(line.at)}</span>
              <span className="rh-a2-last__what">{line.text}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
